import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, RotateCcw, Truck } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import { courierWaitRequested } from '../../../../features/operatorPacking/operatorPackingActions';
import { requestRelabel, submitRelabel } from '../../../../features/operatorPacking/operatorPackingThunks';
import {
  RELABEL_VIEW,
  canSendPacking,
  relabelSheet,
  selectPackingRelabel,
} from '../../../../features/operatorPacking/operatorPackingUtils';

/**
 * Creating the shipment again once the floor changed the items of a labeled
 * order (`relabelView`). Before packing is done it says it will happen; then
 * the server makes it by itself, and the block waits for the new label, says
 * why it failed or cannot be made, and offers it again when that helps —
 * asking first: it cannot be taken back from the phone. Once made, it asks to
 * print the new label in place of the old one.
 *
 * @param {object} props
 * @param {object} props.order
 * @param {boolean} [props.standalone] - On its own, before packing is done; otherwise inside the result.
 */
export const PackingRelabel = ({ order, standalone = false }) => {
  const dispatch = useDispatch();
  const view = useSelector(selectPackingRelabel);
  const locked = useSelector((state) => !canSendPacking(state.operatorPacking));
  const sending = useSelector(selectIsRunning(requestRelabel, order.id));
  const [asking, setAsking] = useState(false);
  if (!view) return null;

  const sheet = relabelSheet(order);
  const madeAgain = view.status === RELABEL_VIEW.DONE && !view.labelFailed;
  const offers = view.status === RELABEL_VIEW.OFFER || (view.status === RELABEL_VIEW.FAILED && view.retryable);

  // Closed whatever the answer: the band says it, and the block what follows.
  const confirm = async () => {
    await dispatch(submitRelabel({ orderId: order.id }));
    setAsking(false);
  };

  return (
    <div
      data-testid="packing-relabel"
      data-status={view.status}
      className={clsx(
        'space-y-2',
        standalone && 'rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]'
      )}
    >
      {view.status === RELABEL_VIEW.WAITING ? (
        <p className="flex items-center gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
          <Loader2 size={18} aria-hidden="true" className="shrink-0 animate-spin text-ninja-green" />
          {view.text}
        </p>
      ) : (
        <p className="flex items-start gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
          {madeAgain ? (
            <CheckCircle2 size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-ninja-green" />
          ) : (
            <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          )}
          <span data-testid="packing-relabel-text">{view.text}</span>
        </p>
      )}

      {offers && (
        <PendingButton
          pendingOn={requestRelabel}
          pendingKey={order.id}
          variant="outline"
          size="lg"
          fullWidth
          icon={view.status === RELABEL_VIEW.OFFER ? Truck : RotateCcw}
          disabled={locked}
          loadingLabel="Creando…"
          onClick={() => setAsking(true)}
          data-testid={view.status === RELABEL_VIEW.OFFER ? 'packing-relabel-send' : 'packing-relabel-retry'}
        >
          {view.action}
        </PendingButton>
      )}
      {view.askSupervisor && <p className="text-sm text-text-secondary">Avísale a tu supervisor.</p>}
      {view.status === RELABEL_VIEW.TIMEOUT && (
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          icon={RefreshCw}
          onClick={() => dispatch(courierWaitRequested(order.id))}
          data-testid="packing-relabel-recheck"
        >
          Revisar de nuevo
        </Button>
      )}

      <ConfirmSheet
        isOpen={asking}
        title={sheet.title}
        message={sheet.message}
        confirmLabel={sheet.confirmLabel}
        busy={sending}
        busyLabel="Creando…"
        onConfirm={confirm}
        onClose={() => setAsking(false)}
        testId="packing-relabel-sheet"
        confirmTestId="packing-relabel-confirm"
      >
        {sheet.warning && (
          <p
            data-testid="packing-relabel-unweighed"
            className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300"
          >
            <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
            {sheet.warning}
          </p>
        )}
      </ConfirmSheet>
    </div>
  );
};
